import { createSlice } from "@reduxjs/toolkit";
import {
  DigitalInvitationDetailsDTO,
  EventTypesDTO,
} from "../../models/DigitalOrder.model";
import {
  createInvitation,
  getDigitalInvitationDetails,
  getEventTypes,
  saveDigitalInvitationKeys,
  sendDigitalInvitationSMS,
} from "../thunks/DigitalOrderThunks";

export interface DigitalOrderState {
  // Get Event Types
  eventTypesLoading: boolean;
  eventTypesError: string | null;
  eventTypes: EventTypesDTO[];

  // Create Invitation
  createLoading: boolean;
  createError: string | null;
  viewLink: string | null;
  editLink: string | null;

  // Get Digital Invitation Details
  detailsLoading: boolean;
  detailsError: string | null;
  detailsChecked: boolean; // true after check completes (whether found or not)
  existingInvitation: DigitalInvitationDetailsDTO | null;

  // Send SMS
  smsSending: boolean;
  smsError: string | null;
  smsSent: boolean;

  // Save Keys
  saveLoading: boolean;
  saveError: string | null;
  savedCelebName: string | null;
  savedCelebPhone: string | null;
}

const initialState: DigitalOrderState = {
  eventTypesLoading: false,
  eventTypesError: null,
  eventTypes: [],

  createLoading: false,
  createError: null,
  viewLink: null,
  editLink: null,

  detailsLoading: false,
  detailsError: null,
  detailsChecked: false,
  existingInvitation: null,

  smsSending: false,
  smsError: null,
  smsSent: false,

  saveLoading: false,
  saveError: null,
  savedCelebName: null,
  savedCelebPhone: null,
};

const digitalOrderSlice = createSlice({
  name: "digitalOrder",
  initialState,
  reducers: {
    clearEventTypesError: (state) => {
      state.eventTypesError = null;
    },
    clearCreateError: (state) => {
      state.createError = null;
    },
    clearInvitationLinks: (state) => {
      state.viewLink = null;
      state.editLink = null;
    },
    clearDetailsError: (state) => {
      state.detailsError = null;
    },
    clearSmsState: (state) => {
      state.smsSending = false;
      state.smsError = null;
      state.smsSent = false;
    },
    clearExistingInvitation: (state) => {
      state.existingInvitation = null;
      state.detailsError = null;
    },
    resetDigitalOrder: (state) => {
      state.viewLink = null;
      state.editLink = null;
      state.createError = null;
      state.createLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Event Types
      .addCase(getEventTypes.pending, (state) => {
        state.eventTypesLoading = true;
        state.eventTypesError = null;
      })
      .addCase(getEventTypes.fulfilled, (state, action) => {
        state.eventTypesLoading = false;
        state.eventTypes = action.payload.eventTypes;
      })
      .addCase(getEventTypes.rejected, (state, action) => {
        state.eventTypesLoading = false;
        state.eventTypesError =
          action.error.message || "Failed to fetch event types";
      })
      // Create Invitation
      .addCase(createInvitation.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createInvitation.fulfilled, (state, action) => {
        state.createLoading = false;
        state.viewLink = action.payload.viewLink;
        state.editLink = action.payload.editLink;
      })
      .addCase(createInvitation.rejected, (state, action) => {
        state.createLoading = false;
        state.createError =
          action.error.message || "Failed to create invitation";
      })
      // Get Digital Invitation Details
      .addCase(getDigitalInvitationDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(getDigitalInvitationDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.detailsChecked = true;
        // Only store invitation if API said success=true
        if (action.payload.success) {
          state.existingInvitation = action.payload.digitalInvitationDetails;
        } else {
          state.existingInvitation = null;
        }
      })
      .addCase(getDigitalInvitationDetails.rejected, (state, action) => {
        state.existingInvitation = null;
        state.detailsLoading = false;
        state.detailsChecked = true;
      })
      // Send SMS
      .addCase(sendDigitalInvitationSMS.pending, (state) => {
        state.smsSending = true;
        state.smsError = null;
        state.smsSent = false;
      })
      .addCase(sendDigitalInvitationSMS.fulfilled, (state) => {
        state.smsSending = false;
        state.smsSent = true;
      })
      .addCase(sendDigitalInvitationSMS.rejected, (state, action) => {
        state.smsSending = false;
        state.smsError =
          action.error.message || "Failed to send SMS";
      })
      // Save Keys
      .addCase(saveDigitalInvitationKeys.pending, (state) => {
        state.saveLoading = true;
        state.saveError = null;
      })
      .addCase(saveDigitalInvitationKeys.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.savedCelebName = action.payload.celebName;
        state.savedCelebPhone = action.payload.celebPhone;
      })
      .addCase(saveDigitalInvitationKeys.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError =
          action.error.message || "Failed to save invitation keys";
      });
  },
});

export const {
  clearEventTypesError,
  clearCreateError,
  clearInvitationLinks,
  clearDetailsError,
  clearExistingInvitation,
  clearSmsState,
  resetDigitalOrder,
} = digitalOrderSlice.actions;
export default digitalOrderSlice.reducer;
